import { parseUnits } from 'ethers/lib/utils'
import { useCallback, useMemo, useEffect, useState } from 'react'
import { MaxUint256 } from '@ethersproject/constants'
import { message } from '~/components/Message'
import {
  ApprovalState,
  useApproveCallback,
  useNeedReduceAllowance2Zero,
} from '~/hooks/useApproval'
import { useModalToggle } from '~/state/application/hooks'
import { ApplicationModal } from '~/state/application/reducer'
import {
  useAllowanceTarget,
  useInputAmountVerify,
  useLimitSwapAmount,
  useLimitSwapPair,
} from '~/state/dex/hooks'
import { useLimitOrderAllowance } from './useLimitOrderAllowance'
import { ApprovalProgress } from '~/components/ApprovalModal/ApproveStatusLabel'
import {
  LimitOrderStatus,
  useGA,
  useUserLimitOrderList,
  useUpdaterChainId,
  useDebounce,
} from '~/hooks'
import { useTranslation } from 'react-i18next'
import { useTokenBalance } from '~/state/wallet/hooks'
import BigNumber from 'bignumber.js'
import { FixedPointNumber } from '~/utils/fixedPoint'
import { WETH as WETHAddress } from '~/constants/addresses'
import { getLOMinTrade } from '~/apis'
import { toBigNumber, toBigNumberWithDecimal } from '~/utils/numbers'
import { BigNumber as EthersBN } from 'ethers'
import useSWR from 'swr'

export const useAllowanceVerify = () => {
  const { t } = useTranslation()
  const { trackClick } = useGA()

  const { inputAmount } = useLimitSwapAmount()
  const [inputToken] = useLimitSwapPair()
  const allowance = useLimitOrderAllowance(inputToken)
  const allowanceTarget = useAllowanceTarget()

  const { data: limitOrders } = useUserLimitOrderList([
    LimitOrderStatus.Pending,
    LimitOrderStatus.PartialFilled,
  ])

  const [needCancelApprove, setNeedCancelApprove] = useState(false)

  const [approvalState, approve] = useApproveCallback(
    inputToken,
    MaxUint256,
    allowanceTarget,
  )

  const [cancelApprovalState, cancelApprove] = useApproveCallback(
    inputToken,
    EthersBN.from(0),
    allowanceTarget,
  )

  const decimals = inputToken?.decimals
  const modalState = useMemo(() => {
    return {
      token: inputToken,
      title: needCancelApprove
        ? t('toggleAllowanceModal.lockdown_title', {
            symbol: inputToken?.symbol,
          })
        : t('toggleAllowanceModal.approve_title', {
            symbol: inputToken?.symbol,
          }),
      description1: t('toggleAllowanceModal.description_1'),
      description2: t('toggleAllowanceModal.description_2'),
      progress: needCancelApprove
        ? ApprovalProgress.CANCEL_APPROVING
        : ApprovalProgress.APPROVING,
      approvalState: needCancelApprove
        ? ApprovalState.APPROVED
        : ApprovalState.NOT_APPROVED,
    }
  }, [inputToken, needCancelApprove])
  const isApproved = useMemo(() => allowance && !allowance.isZero(), [
    allowance,
  ])

  const toggleApprovalModal = useModalToggle(ApplicationModal.APPROVAL_STATUS, {
    stack: true,
    modalState,
  })

  const needReduceAllowance2Zero = useNeedReduceAllowance2Zero(inputToken)

  useEffect(() => {
    needReduceAllowance2Zero().then((result) => {
      setNeedCancelApprove(result)
    })
  }, [
    inputToken,
    allowanceTarget,
    approvalState,
    cancelApprovalState,
    needReduceAllowance2Zero,
  ])

  return useCallback(() => {
    const activeOrderBalance = (limitOrders || [])
      .filter(
        (order) =>
          order.makerToken.toLowerCase() === inputToken.address.toLowerCase(),
      )
      .reduce((acc, order) => {
        return acc.plus(
          (order.status === LimitOrderStatus.Pending
            ? order.makerTokenAmount
            : order.remainInputAmountToDeal) || 0,
        )
      }, new BigNumber(0))

    // is approve with MaxUint256
    if (approvalState === ApprovalState.APPROVED) return true
    if (!allowance) return false

    const formattedAllowance = toBigNumberWithDecimal(allowance, decimals)
    const formattedActiveOrderBalance = toBigNumberWithDecimal(
      activeOrderBalance,
      decimals,
    )

    const needApproveOrReApprove = new FixedPointNumber(
      inputAmount,
      decimals,
    ).gt(formattedAllowance.minus(formattedActiveOrderBalance))

    if (needApproveOrReApprove) {
      if (isApproved && needCancelApprove) {
        message.success(
          t('token_allowance_not_enough_and_need_cancel', {
            token: inputToken.symbol,
          }),
        )
        toggleApprovalModal()
        cancelApprove()
      } else {
        if (isApproved) {
          message.success(
            t('token_allowance_not_enough', { token: inputToken.symbol }),
          )
        }

        toggleApprovalModal()
        approve()
          .then(() => {
            trackClick('webtl_lo_appove_confirm', {
              is_success: 1,
              symbol: inputToken.symbol,
            })
          })
          .catch((e) => {
            trackClick('webtl_lo_appove_confirm', {
              is_success: 0,
              symbol: inputToken.symbol,
              message: e?.message,
            })
          })
      }

      return false
    }
    return true
  }, [
    inputAmount,
    inputToken,
    allowance,
    approvalState,
    isApproved,
    needCancelApprove,
    toggleApprovalModal,
    approve,
    cancelApprove,
  ])
}
export const useAmountVerify = () => {
  const { inputAmount, outputAmount } = useLimitSwapAmount()
  const inputVerify = useInputAmountVerify()
  const { t } = useTranslation()

  return useCallback(() => {
    if (inputVerify(inputAmount) && inputVerify(outputAmount)) {
      return true
    }
    message.error(t('unvalid_input_amount'))
    return false
  }, [inputAmount, outputAmount, t, inputVerify])
}

export const useBalanceVerify = () => {
  const { inputAmount } = useLimitSwapAmount()
  const [inputToken] = useLimitSwapPair()
  const balance = useTokenBalance(inputToken)
  const { t } = useTranslation()
  const chainId = useUpdaterChainId()

  return useCallback(
    (setTipVisible?: (value: boolean) => void) => {
      const isSellWETH =
        inputToken?.address.toLowerCase() ===
        WETHAddress[chainId]?.toLowerCase()
      const isInsufficientBalance = parseUnits(
        inputAmount,
        inputToken.decimals,
      ).gt(balance?.toFixed(0))

      if (isInsufficientBalance) {
        if (!!setTipVisible && isSellWETH) {
          setTipVisible(true)
          setTimeout(() => {
            setTipVisible(false)
          }, 3000)
          return false
        }
        message.error(
          t('$symbol_insufficient_balance', { symbol: inputToken.symbol }),
        )
        return false
      } else return true
    },
    [balance, inputAmount, inputToken],
  )
}

export const useActiveBalanceVerify = () => {
  const { inputAmount } = useLimitSwapAmount()
  const [inputToken] = useLimitSwapPair()
  const balance = useTokenBalance(inputToken)
  const { data: limitOrders, error } = useUserLimitOrderList([
    LimitOrderStatus.Pending,
    LimitOrderStatus.PartialFilled,
  ])
  const { t } = useTranslation()

  return useCallback(() => {
    // fetch orders failed, let backend check
    if (error) return true

    const activeOrderBalance = (limitOrders || [])
      .filter(
        (order) =>
          order.makerToken.toLowerCase() === inputToken.address.toLowerCase(),
      )
      .reduce((acc, order) => {
        return acc.plus(
          (order.status === LimitOrderStatus.Pending
            ? order.makerTokenAmount
            : order.remainInputAmountToDeal) || 0,
        )
      }, new BigNumber(0))

    if (
      new FixedPointNumber(inputAmount, inputToken.decimals).gt(
        toBigNumberWithDecimal(
          balance.minus(activeOrderBalance),
          inputToken.decimals,
        ),
      )
    ) {
      message.error(
        t('sign_limit_order_failed_1008', {
          inputTokenSymbol: inputToken?.symbol,
        }),
      )
      return false
    }
    return true
  }, [balance, limitOrders, inputAmount, t, error, inputToken])
}

export const useTradeVerify = () => {
  const verifyAmount = useAmountVerify()
  const verifyAllowance = useAllowanceVerify()
  const verifyBalance = useBalanceVerify()
  const verifyActiveBalance = useActiveBalanceVerify()

  return useCallback(
    (setTipVisible?: (value: boolean) => void) => {
      if (
        verifyAmount() &&
        verifyBalance(setTipVisible) &&
        verifyActiveBalance() &&
        verifyAllowance()
      )
        return true
      return false
    },
    [verifyAmount, verifyAllowance],
  )
}

export const useSmallOrderVerify = () => {
  const chainId = useUpdaterChainId()
  const [inputToken, outputToken] = useLimitSwapPair()
  const { inputAmount, outputAmount } = useLimitSwapAmount()
  const debouncedInputAmount = useDebounce(inputAmount, 300)
  const debouncedOutputAmount = useDebounce(outputAmount, 300)

  // 为什么不把 inputToken 和 outputToken 也加入到依赖中？
  // 因为改变 token 默认会清空数量（点击切换按钮除外），数量清空就没必要去请求了
  // 点击切换按钮（useDirectionChange）会先 dispatch token 变更，再 dispatch 数量变更。因为有了先后顺序，如果把 token 也加入依赖中会触发两次请求，第一次请求用了旧的数量影响结果
  // 上述两种情况依赖数量变化即可
  const shouldFetch = useMemo(
    () =>
      !!debouncedInputAmount &&
      !!debouncedOutputAmount &&
      !!inputToken?.address &&
      !!outputToken?.address &&
      inputToken?.decimals != null,
    [debouncedInputAmount, debouncedOutputAmount],
  )

  const makerToken = inputToken?.address

  const makerTokenAmount = useMemo(() => {
    if (!shouldFetch) return null

    return toBigNumber(debouncedInputAmount)
      .shiftedBy(inputToken?.decimals)
      .toFixed()
  }, [shouldFetch, debouncedInputAmount])

  const queryKey = useMemo(() => {
    if (!shouldFetch) return null

    return [
      'getLOMinTrade',
      {
        chainId,
        makerToken,
        makerTokenAmount,
      },
    ]
  }, [shouldFetch, chainId, makerTokenAmount])

  const { data } = useSWR(
    queryKey,
    () => getLOMinTrade(chainId, makerToken, makerTokenAmount),
    {
      fallbackData: { isRiskTradeAmount: false, minTradeUSDValue: '1' },
      revalidateOnFocus: false,
      dedupingInterval: 1000,
    },
  )

  return data?.isRiskTradeAmount ?? false
}
